import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { creditsAPI } from '@/services/api';
import { CreditStatus } from '@/types';
import { useAuth } from './AuthContext';
import { useCreditUpdates } from './WebSocketContext';
import { useToast } from './ToastContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useTips } from './TipsContext';

export interface CreditContextValue {
  creditStatus: CreditStatus | null;
  isLoading: boolean;
  error: string | null;
  fetchStatus: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  clearStatus: () => void;
  hasCredits: boolean;
  isLowCredit: boolean;
}

const CreditContext = createContext<CreditContextValue | undefined>(undefined);

interface CreditProviderProps {
  children: ReactNode;
}

export function CreditProvider({ children }: CreditProviderProps) {
  const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, phase } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();

  // Fetch credit status from API
  const fetchStatus = useCallback(async () => {
    // Don't fetch if not authenticated
    if (phase !== 'authenticated' || !user) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const status = await creditsAPI.getStatus();
      setCreditStatus(status);
    } catch (err: any) {
      console.error('Failed to fetch credit status:', err);
      setError(err?.response?.data?.detail || 'Failed to load credit status');
      // Don't clear existing status on error - keep showing stale data
    } finally {
      setIsLoading(false);
    }
  }, [user, phase]);

  // Alias for fetchStatus (semantic naming for after-action refreshes)
  const refreshStatus = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  // Clear status (e.g., on logout)
  const clearStatus = useCallback(() => {
    setCreditStatus(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Auto-fetch on authentication
  useEffect(() => {
    if (phase === 'authenticated' && user) {
      fetchStatus();
    } else if (phase === 'unauthenticated') {
      clearStatus();
    }
  }, [phase, user, fetchStatus, clearStatus]);

  // Auto-refresh when WebSocket delivers a credit update
  const { lastCreditUpdate } = useCreditUpdates();
  const lastHandledTimestampRef = useRef<number>(0);

  useEffect(() => {
    if (
      lastCreditUpdate &&
      lastCreditUpdate.timestamp > lastHandledTimestampRef.current
    ) {
      lastHandledTimestampRef.current = lastCreditUpdate.timestamp;
      if (lastCreditUpdate.action === 'purchase_complete') {
        // Re-fetch credit balance from API
        refreshStatus();
        // Show toast notification
        const count = lastCreditUpdate.creditsAdded ?? 0;
        if (count > 0) {
          showToast(
            t('toast.purchaseSuccess', { count }),
            'success'
          );
        }
      }
    }
  }, [lastCreditUpdate, refreshStatus, showToast, t]);

  // Contextual tips
  const { showTip } = useTips();

  // Computed properties
  const hasCredits = creditStatus
    ? creditStatus.credit_exempt === true
      || (creditStatus.total_available ?? 0) > 0
      || creditStatus.pool_available === true
    : false;

  const isLowCredit = creditStatus?.credit_exempt ? false : (creditStatus?.low_credit_warning ?? false);

  // Show tip when credits drop to 1
  useEffect(() => {
    if (
      creditStatus &&
      !creditStatus.credit_exempt &&
      (creditStatus.total_available ?? 0) === 1
    ) {
      showTip('tip_credit_low');
    }
  }, [creditStatus?.total_available, creditStatus?.credit_exempt, showTip]);

  const value: CreditContextValue = {
    creditStatus,
    isLoading,
    error,
    fetchStatus,
    refreshStatus,
    clearStatus,
    hasCredits,
    isLowCredit,
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditContext);
  if (!ctx) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return ctx;
}

/**
 * Hook for checking credits before an action
 * Returns { canProceed, showBlocker, checkCredits }
 */
export function useCreditCheck() {
  const { creditStatus, hasCredits, refreshStatus } = useCredits();
  const [showBlocker, setShowBlocker] = useState(false);

  const checkCredits = useCallback((): boolean => {
    // For B2B2C students, check pool_available
    if (creditStatus?.usage_hidden) {
      if (creditStatus.pool_available === false) {
        setShowBlocker(true);
        return false;
      }
      return true;
    }

    // For B2C users, check total_available
    if (!hasCredits) {
      setShowBlocker(true);
      return false;
    }

    return true;
  }, [creditStatus, hasCredits]);

  const hideBlocker = useCallback(() => {
    setShowBlocker(false);
  }, []);

  return {
    canProceed: hasCredits,
    showBlocker,
    checkCredits,
    hideBlocker,
    refreshStatus,
    creditStatus,
  };
}

export default CreditContext;
